import { Stack, Switch, FormControlLabel, Typography, Divider } from '@mui/material';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { useUpdateCommunitySettings } from '@/api/endpoints/community/useUpdateCommunitySettings';

import FormTextField from '@/components/form/FormTextField';
import ModalWithForm from '@/components/modals/ModalWithForm';

interface CommunitySettingsRequest {
  name: string;
  description: string;
  require_verification: boolean;
  is_active: boolean;
}

interface CommunitySettingsModalProps {
  communityId: number;
  currentSettings: {
    name: string;
    description: string;
    require_verification: boolean;
    is_active: boolean;
  };
  onClose: () => void;
}

const CommunitySettingsModal: React.FC<CommunitySettingsModalProps> = ({
  communityId,
  currentSettings,
  onClose,
}) => {
  const form = useForm<CommunitySettingsRequest>({
    defaultValues: {
      name: currentSettings.name,
      description: currentSettings.description,
      require_verification: currentSettings.require_verification,
      is_active: currentSettings.is_active,
    },
    mode: 'onBlur',
  });

  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const updateSettingsMutation = useUpdateCommunitySettings({
    onSuccess: () => {
      onClose();
    },
  });

  const onSubmit = useCallback(
    async (data: CommunitySettingsRequest) => {
      await updateSettingsMutation.mutateAsync({
        communityId,
        name: data.name,
        description: data.description,
        require_verification: data.require_verification,
        is_active: data.is_active,
      });
    },
    [updateSettingsMutation, communityId]
  );

  const requireVerification = watch('require_verification');
  const isActive = watch('is_active');

  return (
    <ModalWithForm
      form={form}
      onSubmit={onSubmit}
      onClose={onClose}
      maxWidth="sm"
      title="Community Settings"
      buttonLabel="Save Changes"
    >
      <Stack spacing={3}>
        <FormTextField
          control={control}
          label="Community Name"
          size="small"
          placeholder="Enter community name"
          required
          error={!!errors.name}
          helperText={errors.name?.message}
          {...register('name', {
            required: 'Community name is required',
            minLength: {
              value: 3,
              message: 'Community name must be at least 3 characters long',
            },
            maxLength: {
              value: 100,
              message: 'Community name cannot exceed 100 characters',
            },
          })}
        />

        <FormTextField
          control={control}
          label="Description"
          size="small"
          multiline
          rows={3}
          placeholder="Describe what this community is about..."
          required
          error={!!errors.description}
          helperText={errors.description?.message}
          {...register('description', {
            required: 'Community description is required',
            minLength: {
              value: 10,
              message: 'Description must be at least 10 characters long',
            },
            maxLength: {
              value: 500,
              message: 'Description cannot exceed 500 characters',
            },
          })}
        />

        <Divider />

        <Stack spacing={2}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            Community Access
          </Typography>

          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={e => setValue('is_active', e.target.checked)}
                  color="primary"
                />
              }
              label="Community is active"
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: -1 }}>
              {isActive
                ? 'Community is visible and accepting new members'
                : 'Community is hidden and not accepting new members'}
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={requireVerification}
                  onChange={e => setValue('require_verification', e.target.checked)}
                  color="primary"
                />
              }
              label="Require member verification"
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: -1 }}>
              {requireVerification
                ? 'New members need admin approval before joining'
                : 'Anyone can join this community immediately'}
            </Typography>
          </Stack>
        </Stack>

        <Divider />

        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            <strong>Note:</strong> Changes to verification settings will only affect new members. 
            Existing members will not be affected.
          </Typography>
        </Stack>
      </Stack>
    </ModalWithForm>
  );
};

export default CommunitySettingsModal;